import {  Injectable } from '@angular/core';
import { Observable,  Subject, Subscription } from 'rxjs';
import { BillingService, Subscription as BillingOrder,  
	RatePlanInfo, RatePlanCharge,
	UserSubscriptions, 
	UserLicenses,
	Product,
	ProductRatePlan,
	UserLicense, 
	AccountCreateRequest, 
	BillingAccount, 
	OrderCreateRequest, 
	SubscriptionCollection,
	License,
	OrderCreateInfo,
	LicenseCollection} from '@mitel/cloudlink-sdk/billing';
import { AppUserClaimsService } from './app-user-claims.service';
import { AuthService } from './auth.service';
import { ErrorsHandlerService } from './errors-handler.service';
import { tap, shareReplay, take } from 'rxjs/operators';
import { productLicenses, accountMessages } from '../constants/constants';
import { Account, User } from '@mitel/cloudlink-sdk'; 

import { environment } from '../../../environments/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Util } from '../utils/Util';
import { ProgressIndicatorPopupComponent } from '../../shared/components/progress-indicator-popup/progress-indicator-popup.component';
import { AdminService } from './admin.service';
import { TranslateService } from '@ngx-translate/core';
import { SpinnerService } from './spinner.service';
import * as _ from 'lodash';

export class OrderItem {
	id: string;
	subscriptionNumber: string;
	details: string;
	orderDate: string;
	expired?: boolean;
	perpetual?: boolean;
	expiring: boolean;
	quantity: string;
	remaining: string;
};

export class SubscriptionItem {
	id: string;
	subscriptionNumber: string;
	details: string;
	ratePlanDetails?: string;
	orderDate: string;
	expired?: boolean;
	perpetual?: boolean;
	expiring: boolean;
	quantity: number;
};

export class SubscriptionDetails {
	productName: string;
	isConcurrent: boolean;
	isCounted: boolean;
	totalCount: number;
	used: number;
	overProvisioned: number;
	tierList: SubscriptionDetails[];
	subsList: SubscriptionItem[];
}

@Injectable({
	providedIn: 'root'
})
export class OrdersService {

	public ordersListChanged = new Subject<OrderItem[]>();

	private orders: OrderItem[] = [];
	public remainingLicensesChanged = new Subject<void>();
	public partnerBillingAccountStatusChanged = new Subject<boolean>();
	public updatingLicenses = new Subject<void>();
	private readonly billingService: BillingService; 

	private hasBillingAccount: boolean = undefined;
	public productRatePlanIds;
	private productRatePlanIdsObservable;
	private subscription: Subscription = new Subscription();
	private acctCacheSubscription: Subscription = new Subscription();
	private acctSubTierDetailsCacheSubscription: Subscription = new Subscription();
	private userCacheSubscription: Subscription = new Subscription();

	// only one account can be displayed at a time. 
	// to prevent stale data, clear the list of subscriptions on account change
	private userSubscriptionsForAccountObservable;
	private userSubscriptionsForAccount;

	private acctSubscriptionsForAccountObservable;
	private acctSubscriptionsForAccount;

	private acctSubTierDetailsForAccountObservable;
	private acctSubTierDetailsForAccount;

	constructor( 
		private appUserClaimsSvc: AppUserClaimsService,
		private adminService: AdminService,
		private errorHandlerSvc: ErrorsHandlerService,
		private translateService: TranslateService,
		private modalService: NgbModal,
		private spinnerService: SpinnerService,
		private authService: AuthService ) 
	{
		this.billingService = new BillingService({
			// version: '2019-03-01'
			// cloud: 'dev',
			// stage: 'stage'
		  });
	}

	// awkward code just to find the productName
	findProductName(licenseData: License)
	{
		if ( licenseData && licenseData.tags )
		{
			for ( var i = 0; i < licenseData.tags.length; i++ )
			{
				if ( licenseData.tags[i].name == "productName" )
					return licenseData.tags[i].value;
			}
		}

		// if we get here the product name was not in the tags so lets use the old way
		if ( licenseData && licenseData._embedded && licenseData._embedded.subscriptions &&
			licenseData._embedded.subscriptions[0] && licenseData._embedded.subscriptions[0]._embedded &&
			licenseData._embedded.subscriptions[0]._embedded.ratePlans && licenseData._embedded.subscriptions[0]._embedded.ratePlans._embedded &&
			licenseData._embedded.subscriptions[0]._embedded.ratePlans._embedded.items && licenseData._embedded.subscriptions[0]._embedded.ratePlans._embedded.items[0])
		{
			return licenseData._embedded.subscriptions[0]._embedded.ratePlans._embedded.items[0].productName
		}

		return undefined;
	}

	findTierName(licenseData: License)
	{
		if ( licenseData && licenseData.tags )
		{
			for ( var i = 0; i < licenseData.tags.length; i++ )
			{
				if ( licenseData.tags[i].name == "tier" && licenseData.tags[i].value == "none")
					return undefined;
				else
					return licenseData.tags[i].value;
			}
		}
	}

	createSubDetailsFromLicense( license: License, name: string ): SubscriptionDetails
	{
		let tempSubDetail = new SubscriptionDetails();
		tempSubDetail.productName = name;
		tempSubDetail.isConcurrent = false;
		tempSubDetail.isCounted = true;
		if ( license.used == -1 ) {
			tempSubDetail.isConcurrent = true;
			tempSubDetail.isCounted = false;
		} else if ( license.used == -2 ) {
			tempSubDetail.isCounted = false;
		}
		tempSubDetail.used = license.used;
		tempSubDetail.totalCount = license.available;
		tempSubDetail.overProvisioned = 0;
		tempSubDetail.subsList = [];
		tempSubDetail.tierList = [];
		return tempSubDetail;
	}

	getAccountSubscriptionDetails(): Promise<SubscriptionDetails[]> {
		console.log("getAccountSubscriptionDetails");
		let params = {};
		let subDetailsList: SubscriptionDetails[] = [];
		return this.getCachedAcctSubTierDetails().then( licenseCollection => {
			console.log("This is the data returned by the getLicensesByAccount", licenseCollection );

			if ( licenseCollection && 
				licenseCollection.count > 0 && 
				licenseCollection._embedded && 
				licenseCollection._embedded.items ) 
			{
				console.log("The licenses from the back end", licenseCollection._embedded.items);
				let licenseList: License[] = licenseCollection._embedded.items;

				for ( var i = 0; i < licenseList.length; i++ )
				{
					// need the product name to determine we have an entry for it yet
					let productName = this.findProductName(licenseList[i]);
					if ( productName != undefined )
					{
						let subDetail = subDetailsList.find( item => {
							return item.productName == productName;
						});

						if ( subDetail == undefined ) // not in the list yet, add it
						{
							subDetail = this.createSubDetailsFromLicense( licenseList[i], productName );
							subDetailsList.push( subDetail );
						}
						else
						{
							// there is an entry already, just add to the totals
							subDetail.totalCount += licenseList[i].available;
							subDetail.used += licenseList[i].used;
						}

						// is this a tiered product?
						let tierName = this.findTierName( licenseList[i] );
						let tierDetail = undefined;
						if ( tierName != undefined )
						{
							tierDetail = subDetail.tierList.find( item => {
								return item.productName == tierName;
							});
							if ( tierDetail == undefined ) // not in the list yet, add it
							{
								tierDetail = this.createSubDetailsFromLicense( licenseList[i], tierName );
								subDetail.tierList.push( tierDetail );
							}
							else
							{
								// there is an entry already, just add to the totals
								tierDetail.totalCount += licenseList[i].available;
								tierDetail.used += licenseList[i].used;
							}
						}

						let acctSubList: BillingOrder[] = licenseList[i]._embedded.subscriptions; 
						for ( var j = 0; j < acctSubList.length; j++ )
						{
							if ( acctSubList[j]._embedded &&
								acctSubList[j]._embedded.ratePlans &&
								acctSubList[j]._embedded.ratePlans.count > 0 && 
								acctSubList[j]._embedded.ratePlans._embedded &&
								acctSubList[j]._embedded.ratePlans._embedded.items )
							{
								let ratePlans: RatePlanInfo[] = acctSubList[j]._embedded.ratePlans._embedded.items;
								for ( var l = 0; l < ratePlans.length; l++ )
								{
									if ( ratePlans[l]._embedded &&
										ratePlans[l]._embedded.charges &&
										ratePlans[l]._embedded.charges.count > 0 &&
										ratePlans[l]._embedded.charges._embedded &&
										ratePlans[l]._embedded.charges._embedded.items )
									{
										let charges: RatePlanCharge[] = ratePlans[l]._embedded.charges._embedded.items;
					
										for ( var m = 0; m < charges.length; m++ )
										{
											let newOrder = new SubscriptionItem();
											newOrder.id = acctSubList[j].id;
											// no longer use contractEffectiveDate, was told to use termStartDate
											newOrder.orderDate = this.formatDate(acctSubList[j].termStartDate);
											newOrder.subscriptionNumber = acctSubList[j].subscriptionNumber;
						
											newOrder.expiring = (acctSubList[j].activeState ? (acctSubList[j].activeState == 'expiring' ? true : false) : false);
											newOrder.expired = (acctSubList[j].activeState ? (acctSubList[j].activeState == 'expired' ? true : false) : false);
											newOrder.perpetual = (acctSubList[j].activeState ? (acctSubList[j].activeState == 'perpetual' ? true : false) : false);

											newOrder.details = ratePlans[l].productName + (ratePlans[l].name ? ': ' + ratePlans[l].name : '');
											newOrder.ratePlanDetails = charges[m].name;
											newOrder.quantity = (charges[m].quantity ? parseInt(charges[m].quantity) : 0);
											if ( tierName == undefined )
											{
												subDetail.subsList.push(newOrder);
											}
											else
											{
												tierDetail.subsList.push(newOrder);
											}
											
										}
									}
								}
							}
						}
					}

				}
				// finish by calculating the product totals
				for ( var m = 0; m < subDetailsList.length; m++ )
				{
					// if it is a tiered product, re-calculate the totals for the top
					// level product based on the calculations for the child tiers 
					if ( subDetailsList[m].tierList.length > 0 )
					{
						subDetailsList[m].used = 0;
						subDetailsList[m].overProvisioned = 0;
						subDetailsList[m].totalCount = 0;
						for ( var o = 0; o < subDetailsList[m].tierList.length; o++ )
						{
							if ( subDetailsList[m].tierList[o].used > subDetailsList[m].tierList[o].totalCount )
							{
								subDetailsList[m].tierList[o].overProvisioned = 
									subDetailsList[m].tierList[o].used - subDetailsList[m].tierList[o].totalCount;
								subDetailsList[m].tierList[o].used = subDetailsList[m].tierList[o].totalCount;
							}
							subDetailsList[m].used += subDetailsList[m].tierList[o].used;
							subDetailsList[m].overProvisioned += subDetailsList[m].tierList[o].overProvisioned;
							subDetailsList[m].totalCount += subDetailsList[m].tierList[o].totalCount;
						}
					}
					else
					{
						// calculate overprovisioned for all other products
						if ( subDetailsList[m].used > subDetailsList[m].totalCount )
						{
							subDetailsList[m].overProvisioned = subDetailsList[m].used - subDetailsList[m].totalCount;
							subDetailsList[m].used = subDetailsList[m].totalCount;
						}
					}
				}

				console.log("returning the order list ", subDetailsList);
				return subDetailsList;
			}
			else
			{
				console.log("returning an empty list", subDetailsList);
				return subDetailsList;
			}
		})
	}

	private expiryInMilliseconds: number = 90*24*60*60*1000; //330*24*60*60*1000; 
	private calculateDaysToExpiry( endDateStr: string )
	{
		let d = new Date();
		d.setHours(0,0,0,0); // compare at midnight since we are interested in dates
		let currentTime = d.getTime();
		let specifiedDate = Date.parse( endDateStr + " 00:00:00" ); // compare at midnight since we are interested in dates
		let timeGap = specifiedDate - currentTime;
		if ( timeGap < this.expiryInMilliseconds )
		{
			if ( timeGap < 0 )
				return 0;
			else
			{
				let days = Math.trunc(timeGap/(24*60*60*1000));
				return days;
			}
		}
		return -1;
	}

	// based on a known format
	private formatDate( dateStr: string ): string {
		let dateparts = dateStr.split('-');
		return dateparts[1] + '/' + dateparts[2] + '/' + dateparts[0].substr(2);
	}

	private getUsersSubscriptionsObservable(): Observable<UserSubscriptions[]>
	{
		return Observable.create( async (observer) => {
			let userSubs: UserSubscriptions[] = [];
			return this.billingService.getUsersSubscriptions({}).then(userSubscriptions => {
				if ( userSubscriptions._embedded && userSubscriptions._embedded.items )
				{
					userSubs = userSubscriptions._embedded.items;
				}
			//	console.log("*** CACHE NEW User subscriptions returned from back end", userSubs);
				return observer.next(userSubs);
			})
			.catch( err => {
				//we will log and return an empty list
				console.error("There was an error requesting the user subscriptions", err);
				return observer.next(userSubs);
			});
		})
		.pipe(
			tap(() => { console.log('getUsersSubscriptionsObservable method executed')}),
			shareReplay()
		);
	}


	getCachedUserSubscriptions(): Promise<UserSubscriptions[]> 
	{
		return new Promise<UserSubscriptions[]> ( (resolve, reject) => {
			if ( this.userSubscriptionsForAccount != undefined )
			{
			//	console.log("CACHE ***returning USER Cache ", this.userSubscriptionsForAccount)
				return resolve(this.userSubscriptionsForAccount);
			}
			else
			{
				// no cache, get a fresh list
				if ( this.userSubscriptionsForAccountObservable == undefined )
				{
			//		console.log("CACHE ***>No USER Cache requesting new")
					this.userSubscriptionsForAccountObservable = this.getUsersSubscriptionsObservable();
				}
				this.userCacheSubscription.add(this.userSubscriptionsForAccountObservable.subscribe(userSubs => {
			//		console.log("CACHE*** NEW USER SUBS", userSubs)
					this.userSubscriptionsForAccount = userSubs;
					return resolve(userSubs);
				}));
			}

		});
	}

	public clearUserSubscriptionsCache()
	{
		this.userCacheSubscription.unsubscribe();
		this.userCacheSubscription = new Subscription();
		this.userSubscriptionsForAccountObservable = undefined;
		this.userSubscriptionsForAccount = undefined;
	//	console.log("CACHE done clearing the USER CACHE")
	}

	// Temporary commenting it out till we know that new implementation of getUsersLicensesFromCachedList works well.
	// If it is established that new implementation works, then we can remove this piece of code and the associated skipped tests.
	// public getUsersLicensesFromCachedList( userId: string ): Promise<any>
	// {
	// 	return new Promise<any> ( (resolve, reject) => {
	// 		let licenses = [];

	// 		return this.getCachedUserSubscriptions().then(userSubs => {
	// 			// determine if the user is in the subscription list
	// 			if ( userSubs && userSubs.length > 0 )
	// 			{
	// 				let foundUserSubs = userSubs.filter( sub => {
	// 					return sub.uniqueUserId == userId;
	// 				});

	// 				// if we have a user, now map the subscription to a license
	// 				if ( foundUserSubs && foundUserSubs.length > 0 )
	// 				{
	// 					this.subscription.add(this.productRatePlanIdsObservable.subscribe(async (productRatePlanIds) => 
	// 					{ 
	// 						console.log("These are the products", productRatePlanIds)
	// 						if ( productRatePlanIds )
	// 						{
	// 							for ( var i = 0; i < foundUserSubs.length; i++ )
	// 							{
	// 								let foundLicense = Object.keys(productRatePlanIds).find( product => {
	// 									console.log("Analyze product", product, productRatePlanIds[product]);
	// 									if ( productRatePlanIds[product].length > 0 && productRatePlanIds[product].includes(foundUserSubs[i].productRatePlanId) )
	// 									{
	// 										//we have a match
	// 										console.log("we have a match.", product)
	// 										return true;
	// 									}
	// 									return false;
	// 								});
	// 								if ( foundLicense )
	// 									licenses.push(foundLicense);
	// 							}
	// 							return resolve( licenses );
	// 						}
	// 						else{
	// 							return resolve( licenses );
	// 						}
	// 					}));
	// 				}
	// 				else
	// 				{
	// 					return resolve( licenses );
	// 				}
	// 			}
	// 			else
	// 			{
	// 				return resolve( licenses );
	// 			}
	// 		});
	// 	});
	// }

	public getUsersLicensesFromCachedList( userId: string ): Promise<any>
	{
		let licenses = [];
		return this.getCachedUserSubscriptions().then(userSubs => {
			// determine if the user is in the subscription list
			if ( userSubs && userSubs.length > 0 )
			{
				let foundUserSubs = userSubs.filter( sub => {
					return sub.uniqueUserId == userId;
				});

				// if we have a user, now map the subscription to a license
				if ( foundUserSubs && foundUserSubs.length > 0 )
				{
					for ( let i = 0; i < foundUserSubs.length; i++ ) {
						let licenseName = this.findLicenseName(foundUserSubs[i]);
						if(licenseName)
							licenses.push(licenseName);
					}
				}
			}
			// this log is seen too much but I will leave it here
			// for dev debug purposes
		//	console.log("Licenses for user: ", userId, licenses);
			return licenses;
		});
	}

	// private calculateNumberUsedLicenses( productRatePlanId: string, subscriptionId: string, userSubs: UserSubscriptions[] )
	// {
	// 	let count: number = 0;
	// 	for ( var i = 0; i < userSubs.length; i++ )
	// 	{
	// 		if ( (userSubs[i].productRatePlanId === productRatePlanId) && (subscriptionId === userSubs[i].subscriptionId) )
	// 		{
	// 			if ( userSubs[i].quantity ) // sometimes it is not supplied
	// 				count += userSubs[i].quantity;
	// 		}
	// 	}
	// 	return count;
	// }

	// we will save the state for the login session to prevent unnecessary querying
	hasABillingAccount(): Promise<boolean>
	{
		console.log(">>>>Determining if logged in user has a billing account", this.hasBillingAccount);
		if ( this.hasBillingAccount != undefined )
			return Promise.resolve(this.hasBillingAccount);

		console.log("Need to request if there is a billing account for the logged in user")
		if ( this.appUserClaimsSvc.isAssumedRole() )
		{
			console.log("requesting the PARTNERS billing account")
			return this.billingService.getPartnerBillingAccount({}).then(result => {
				console.log("Result from getPartnerBillingAccount", result);
				this.hasBillingAccount = true;
				return true;
			})
			.catch(err => {
				// if there is an error, then we assume there is no account
				if ( err.statusCode == 400 || 
					(err.body && err.body.message && err.body.message.indexOf("is no billing account")) > -1 )
				{
					this.hasBillingAccount = false;
					return false;
				}
				else
				{	// not setting the state allows us to query again the next time
					console.error("ERROR: Requesting if there is a billing account (getPartnerBillingAccount)", err);
					return false;
				}
			});
		}
		else
		{
			// customer accounts do not have a billing account so we will not even attempt to request it
			let claims = this.appUserClaimsSvc.getClaims();
			// will only request for PARTNER_ADMINS
			if ( claims.role == 'PARTNER_ADMIN' )
			{
				console.log("requesting the normal billing account")
				return this.billingService.getBillingAccount({}).then(result => {
					console.log("Result from getBillingAccount", result);
					this.hasBillingAccount = true;
					return true;
				})
				.catch(err => {
					// if there is an error, then we assume there is no account
					if ( err.statusCode == 400 || 
						(err.body && err.body.message && err.body.message.indexOf("is no billing account")) > -1 )
					{
						this.hasBillingAccount = false;
						return false;
					}
					else
					{	// not setting the state allows us to query again the next time
						console.error("ERROR: Requesting if there is a billing account (getBillingAccount)", err);
						return false;
					}
				});
			}
			else
			{
				return Promise.resolve(false);
			}
		}	
	}

	public getUserLicenses(userId: string): Promise<UserLicenses> {
		console.log('fetching user licenses');
		return new Promise<any>(async (resolve, reject) => {
			try {
				console.time('getlicense')
				const userLicense = await this.billingService.getUserLicense({userId});
				console.timeEnd('getlicense')
				resolve(userLicense);
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				reject(err);
			}
		});
	}	

	getRemainingLicenses(detailed?:boolean): Promise<any> {
		console.log("call to get remaining licenses");
		let remainingLicenses = {};
		let subDetailsList: SubscriptionDetails[] = [];

		return this.billingService.getLicencesByAccount({}).then( licenseCollection => {
			console.log("This is the data returned by the getLicensesByAccount", licenseCollection );

			if ( licenseCollection && 
				licenseCollection.count > 0 && 
				licenseCollection._embedded && 
				licenseCollection._embedded.items ) 
			{
				console.log("The licenses from the back end", licenseCollection._embedded.items);
				let licenseList: License[] = licenseCollection._embedded.items;

				for ( var i = 0; i < licenseList.length; i++ )
				{
					// only for user licenses not concurrent
					if ( licenseList[i].used != -1 )
					{
						// need the product name to determine we have an entry for it yet
						let licenseName = this.findLicenseName(licenseList[i]);
						if ( licenseName != undefined )
						{
							let subDetail = subDetailsList.find( item => {
								return item.productName == licenseName;
							});

							if ( subDetail == undefined ) // not in the list yet, add it
							{
								subDetail = new SubscriptionDetails();
								subDetail.productName = licenseName;
							
								subDetail.used = licenseList[i].used;
								subDetail.totalCount = licenseList[i].available;
								subDetail.overProvisioned = 0;
								subDetailsList.push( subDetail );
							}
							else
							{
								// there is an entry already, just add to the totals
								subDetail.used += licenseList[i].used;
								subDetail.totalCount += licenseList[i].available;
							}

						}
					}

				}
				// calculating over provisioned licenses
				for ( var m = 0; m < subDetailsList.length; m++ )
				{
					if ( subDetailsList[m].used > subDetailsList[m].totalCount )
					{
						subDetailsList[m].overProvisioned = subDetailsList[m].used - subDetailsList[m].totalCount;
						subDetailsList[m].used = subDetailsList[m].totalCount;
					}
					if(detailed) {
						remainingLicenses[subDetailsList[m].productName] = {
																				remaining: subDetailsList[m].totalCount - subDetailsList[m].used,
																				total: subDetailsList[m].totalCount,
																				used: subDetailsList[m].used
																			};
					} else {
						remainingLicenses[subDetailsList[m].productName] = subDetailsList[m].totalCount - subDetailsList[m].used;
					}
				}
				console.log("returning the remaining licenses ", remainingLicenses);
				return remainingLicenses;
			}
			else
			{
				console.log("returning the empty remaining licenses ", remainingLicenses);
				return remainingLicenses;
			}
		});
	}

	findLicenseName(licenseData: License | UserSubscriptions){
		if(licenseData.tags) {
			let licenseName = licenseData.tags.find(tag => tag.name === "tier" && tag.value != "none")?.value;
			if(!licenseName) {
				licenseName = licenseData.tags.find(tag => tag.name === "productName" && tag.value)?.value;
			}
			return licenseName;
		}
	}
	// not confident in the new api, kweeping this code for now
	// 	return this.getCachedAcctSubscriptions().then( async subscriptionCollection => {
	// //	return this.billingService.getSubscriptions(params).then( async subscriptionCollection => {
	// 		if ( subscriptionCollection && 
	// 			subscriptionCollection.count > 0 && 
	// 			subscriptionCollection._embedded && 
	// 			subscriptionCollection._embedded.items ) 
	// 		{
	// 			console.log("The subscriptions from the back end", subscriptionCollection._embedded.items);
	// 			let acctSubList: BillingOrder[] = subscriptionCollection._embedded.items; 
	// 		//	let userSubs: UserSubscriptions[] = await this.getUsersSubscriptions();
	// 			let userSubs: UserSubscriptions[] = await this.getCachedUserSubscriptions();

	// 			for ( let j = 0; j < acctSubList.length; j++ )
	// 			{
	// 				if ( acctSubList[j]._embedded &&
	// 					acctSubList[j]._embedded.ratePlans &&
	// 					acctSubList[j]._embedded.ratePlans.count > 0 && 
	// 					acctSubList[j]._embedded.ratePlans._embedded &&
	// 					acctSubList[j]._embedded.ratePlans._embedded.items )
	// 				{
	// 					if (acctSubList[j].termEndDate != undefined )
	// 					{
	// 						let numDays = this.calculateDaysToExpiry(acctSubList[j].termEndDate);
	// 						if ( numDays === 0 )
	// 						{
	// 							continue;
	// 						}
	// 					}

	// 					// see https://github.com/mitel-networks/cloudlink-console-accounts/issues/847
	// 					// apparently there can be multiple rate plans that are the same
	// 					// we need to combine these rate plans for the final list only showing 1, and thus
	// 					// the remaining license count to be correct as well
	// 					// the decision was to combine based on the productName field
	// 					let fullRatePlans: RatePlanInfo[] = acctSubList[j]._embedded.ratePlans._embedded.items;
	// 					let ratePlans: RatePlanInfo[] = this.collapseRatePlans( fullRatePlans );
			
	// 					for ( let l = 0; l < ratePlans.length; l++ )
	// 					{
	// 						if (!remainingLicenses[ratePlans[l].productName]) {
	// 							remainingLicenses[ratePlans[l].productName] = 0;
	// 							console.log('Inside processing', remainingLicenses);
	// 						}
	// 						if ( ratePlans[l]._embedded &&
	// 							ratePlans[l]._embedded.charges &&
	// 							ratePlans[l]._embedded.charges.count > 0 &&
	// 							ratePlans[l]._embedded.charges._embedded &&
	// 							ratePlans[l]._embedded.charges._embedded.items )
	// 						{
	// 							let charges: RatePlanCharge[] = ratePlans[l]._embedded.charges._embedded.items;
			
	// 							for ( let m = 0; m < charges.length; m++ )
	// 							{
	// 								if ( charges[m].type === 'Recurring' ) {
	// 									if ( charges[m].quantity ) // sometimes the quantity is not supplied
	// 										remainingLicenses[ratePlans[l].productName] +=  ( (+charges[m].quantity) - this.calculateNumberUsedLicenses(ratePlans[l].productRatePlanId, acctSubList[j].subscriptionNumber, userSubs));
	// 								}
	// 							}
	// 						}
			
	// 					}
	// 				}
	// 			}
	// 			console.log("returning the remaining licenses ", remainingLicenses);
	// 			return remainingLicenses;
	// 		} else {
	// 			console.log("returning an empty remaining License\'s list", remainingLicenses);
	// 			return remainingLicenses;
	// 		}
	// 	});
	// }

	getProductRatePlanIdsObservable(): Observable<any> {
		return Observable.create( async (observer) => {
				let tempObject = {};
				try {
					let productCollection = await this.billingService.getProducts({});
					console.log("This is the list of products", productCollection)
					if (productCollection && 
						productCollection.count > 0 &&
						productCollection._embedded &&
						productCollection._embedded.items &&
						productCollection._embedded.items.length > 0
						) {
							let products: Product[] = productCollection._embedded.items;
							for( let i = 0; i < products.length ; i++) {
								
								if ( products[i]._embedded &&
									products[i]._embedded.ratePlans &&
									products[i]._embedded.ratePlans.count > 0 && 
									products[i]._embedded.ratePlans._embedded &&
									products[i]._embedded.ratePlans._embedded.items 
									) {
										tempObject[products[i].name] = [];
										let ratePlans: ProductRatePlan[] = products[i]._embedded.ratePlans._embedded.items;
										for ( let j = 0 ; j < ratePlans.length ; j++) {
											tempObject[products[i].name].push(ratePlans[j].id);
										}
									}
							}
						}
					this.productRatePlanIds = tempObject;
					console.log("This is the list of cached product data", this.productRatePlanIds)
					observer.next(this.productRatePlanIds);
				} catch (err) {
					observer.next({error: err});
				}	
		}).
		pipe(
			tap(() => { console.log('getProductRatePlanIds method executed')}),
			shareReplay()
		);
	}

	getAllProductRatePlanIds(tries?: number) {
		if (tries === undefined || tries > 0) {
			this.productRatePlanIdsObservable = undefined;
			this.productRatePlanIdsObservable = this.getProductRatePlanIdsObservable();
			this.subscription.add(this.productRatePlanIdsObservable.subscribe((productratePlanIds) => {
				if ( productratePlanIds.error ) {
					console.log('Failed to fetch productRatePlanIds', productratePlanIds.error);
					tries === undefined ? this.getAllProductRatePlanIds(2) : this.getAllProductRatePlanIds(--tries); 
				} else {
					console.log('product rate plan ids fetched successfully', productratePlanIds);
				}
			}));
		}
	}

	removeAllUsersLicenses(accountId: string, productName: string) {

		if ( this.productRatePlanIdsObservable == undefined )
			this.getAllProductRatePlanIds();
			
		this.subscription.add(this.productRatePlanIdsObservable.subscribe(async (productratePlanIds) => { 
			if (!productratePlanIds.error) {
				this.spinnerService.setSpinnerMessageByKey("spinnerMessages.remove_"+productLicenses[productName]);
				this.spinnerService.showSpinner();
			//	const userSubs: UserSubscriptions[] = await this.getUsersSubscriptions();
				const userSubs: UserSubscriptions[] = await this.getCachedUserSubscriptions();
				let userWithLicenses: UserSubscriptions[] = [];
				//fetching all the userIds which has specified license to be removed
				for (let i = 0; i < userSubs.length; i++) {
					if (productratePlanIds[productName] &&
						productratePlanIds[productName].length > 0 && 
						(productratePlanIds[productName]).includes(userSubs[i].productRatePlanId)) { 
							userWithLicenses.push(userSubs[i]);
						}
				}
				this.spinnerService.hideSpinner();

				if (userWithLicenses.length > 0 ) {
					console.time("Removing licenses time");
						let failedLicenses: any[] = [];

						let progressUpdate = new Subject();

						let progressData = 
						{
							total: userWithLicenses.length,
							done: 0,
							errorMessage: undefined,
							title: 'users_dashboard.remove_'+productLicenses[productName]+'_title',
							actionString: 'users_dashboard.remove_'+productLicenses[productName]+'_action',
							successMessage: undefined,
							updateSubject: progressUpdate
						}
						let modal = this.modalService.open(ProgressIndicatorPopupComponent, {
							backdrop: 'static',
							keyboard: false,
							centered: true,
						});
				
						modal.componentInstance.progressData = progressData;
						for (let i = 0; i < userWithLicenses.length; i++) {
							// to prevent the client from hammering the system
							await Util.timeout(50);
							try {
								await this.addOrRemoveUserLicenseWithoutErrorHandler(userWithLicenses[i].uniqueUserId, productLicenses[productName], false);
								console.log("Update user license succeeded");
							} catch (error) {
								let stillAnError = true;
								if ( error.statusCode == 500 || error.toString().indexOf( "Failed to fetch" ) != -1 ) {
									try {
										// this error can occur if the system is too loaded. We will try again once before we declare it an error
										await Util.timeout(100);
										await this.addOrRemoveUserLicenseWithoutErrorHandler(userWithLicenses[i].uniqueUserId, productLicenses[productName], false )
										console.log("Second attempt worked!")
										stillAnError = false;
									}
									catch ( err2 )
									{
										//failed again. We will just handle it as an error
										console.error("Second attempt to remove license failed too for user", userWithLicenses[i].uniqueUserId, err2)
									}
								}
								if ( stillAnError === true )
								{
									console.error(" Error removing license for user ", userWithLicenses[i].uniqueUserId, error );
									let user: User;
									// Using the getUserByEmailAddress because same api call is used to fetch the user by email and by userID
									// also because this method has no error handler and notifier.
									try {
										user = await this.adminService.getUserByEmailAddress(accountId,userWithLicenses[i].uniqueUserId);
									} catch(err) {
										user = {
											displayName: this.translateService.instant("users_dashboard.remove_license_failed_user_fetch") + userWithLicenses[i].uniqueUserId
										}
									}
									let errorInfo = {
										user: user,
										error: error
									}
									failedLicenses.push(errorInfo);
									if ( progressData.errorMessage == undefined )
									{
										progressData.errorMessage = this.translateService.instant("users_dashboard.remove_"+productLicenses[productName]+"_failed") + ": <br/>" + user.displayName;
									}
									else // just append the name after a newline
									{
										progressData.errorMessage = progressData.errorMessage + "<br/>" + user.displayName;
									}
								}
							}
							if (i === 0) {
								this.updatingLicenses.next();
							}
							progressData.done++;
							progressUpdate.next( progressData );
						}
						console.timeEnd("Removing licenses time");
						this.clearUserSubscriptionsCache();
						this.remainingLicensesChanged.next();
						progressData.successMessage = this.translateService.instant("users_dashboard.remove_"+productLicenses[productName]+"_done", { numUsers: userWithLicenses.length - failedLicenses.length } );
						progressUpdate.next( progressData );
				}
				
			}
		}));
	}

	addOrRemoveUserLicense(userId: string, licenseName: UserLicense.NameEnum, enableLicense: boolean): Promise<any> {
		return new Promise<any>(async (resolve, reject) => {
			try {
				const body: UserLicenses = [
					{
						name: licenseName,
						enable: enableLicense,
						count: 1
					}
				];
				console.time("setlicense")
				await this.billingService.updateUserLicense(({userId, body}));
				console.timeEnd("setlicense")
				console.log('Update user license was successful');
				resolve('Success');
			} catch (err) {
				this.errorHandlerSvc.handleError(err);
				reject(err);
			}
		}); 
	}

	// this is separate call to remove the error handling which interrupts bulk edit
	addOrRemoveUserLicenseWithoutErrorHandler(userId: string, licenseName: UserLicense.NameEnum, enableLicense: boolean): Promise<any> 
	{
		const body: UserLicenses = [
			{
				name: licenseName,
				enable: enableLicense,
				count: 1
			}
		];
		return this.billingService.updateUserLicense(({userId, body}));
	}

	addOrRemoveMultipleUserLicense(userId: string, body: UserLicenses) {
		return this.billingService.updateUserLicense(({userId, body}));
	}

	// this method is written as being called when the PARTNER is logged in and is not assumed role
	isPartnerBillingAccountCreated( account: Account ): Promise<boolean>
	{
		if ( this.hasBillingAccount === true ) // partner billing account
		{
			return Promise.resolve(true);
		}
		else
		{
			return Promise.resolve(false);
		}
	}

	getBillingAccount() {
		return this.billingService.getBillingAccount({});
	}

	//must be in the role of the account
	createBillingAccount(isPartner: boolean, currency? : string): Promise<BillingAccount>
	{
		let req : any = {
			family: 'mitel',
			autoPay: false
		};

		if ( currency )
			req.currency = currency;

		if ( isPartner == true )
		{
			//only create partner billing account
			console.log("Creating a partner  billing account", req);

			return this.billingService.createBillingAccount( {body:req} ).then(account => {
				console.log("Created billing account", account);
				if ( isPartner == true )
				{
					this.hasBillingAccount = true;
					this.partnerBillingAccountStatusChanged.next(true);
				}
				return account;
			})
			.catch(err => {
				console.error("Received an error from create billing account, but is it an error? ", err);
				if ( err.statusCode == 400 &&
					(err.body && err.body.message && err.body.message.indexOf("is already in use, please enter another account number")) > -1 )
				{
					return {};
				}
				throw err;
			});
		}
		else
		{
			Promise.resolve({} as any);
		}
	}

	private appendLeadingZeroes(n)
	{
		if ( n <= 9 )
		{
		  return "0" + n;
		}
		return n
	}

	// generate todays date in the format the micro expects "YYYY-MM-DD"
	createCurrentDataInFormatForOrder(): string
	{
		let d = new Date();
		let currentDate = d.getFullYear() + '-' + 
						this.appendLeadingZeroes(d.getMonth()+1) + '-' + 
						this.appendLeadingZeroes(d.getDate());
		return currentDate;
	}

	

	createAnOrderForSpecifiedProduct( 
		partnerAccountId: string, 
		productRatePlanChargeId: string, 
		productRatePlanId: string, 
		price: number,
		quantity: number,
		period: number,
		periodType: string ): Promise<void | OrderCreateInfo>
	{
		console.log("CREATE an order for on partner account", partnerAccountId);

		let currentDate = this.createCurrentDataInFormatForOrder();

		console.log("Current date formated", currentDate);
		let req : any = { // tbd  } OrderCreateRequest = {
				"existingAccountNumber": partnerAccountId,
				"orderDate": currentDate,
				"subscriptions": [
					{
					"orderActions": [
						{
						"createSubscription": {
							"subscribeToRatePlans": [
							{
								"chargeOverrides": [
								{
									"pricing": {
									"recurringPerUnit": {
										"listPrice": price,
										"quantity": quantity
									}
									},
									"productRatePlanChargeId": productRatePlanChargeId
								}
								],
								"productRatePlanId": productRatePlanId
							}
							],
							"terms": {
							"autoRenew": false,
							"initialTerm": {
								"period": period,
								"periodType": periodType,
								"startDate": currentDate,
								"termType": "TERMED"
							},
							"renewalSetting": "RENEW_WITH_SPECIFIC_TERM",
							"renewalTerms": [
								{
								"period": 0,
								"periodType": "Month"
								}
							]
							}
						},
						"triggerDates": [
							{
							"name": "ContractEffective",
							"triggerDate": currentDate
							},
							{
							"name": "ServiceActivation",
							"triggerDate": currentDate
							},
							{
							"name": "CustomerAcceptance",
							"triggerDate": currentDate
							}
						],
						"type": "CreateSubscription"
						}
					]
					}
				]
		};
		console.log("Creating order with this information", req);
		return this.billingService.createOrder({body:req}).then(orderCreateInfo => {
			console.log("Order created", orderCreateInfo)
			return orderCreateInfo;
		})
		.catch( err => {
			console.error("Error creating order", err);
			throw err;
		});

	}


	assignTheOrder( order:any, accountNumber: string ): Promise<any>
	{
		console.log("Using the order created to assign", order);
		let currentDate = this.createCurrentDataInFormatForOrder();

		let req : any = { // OrderCreateRequest = {
			orderDate: currentDate,  
			existingAccountNumber: order.accountNumber,
			subscriptions: [
				{
					orderActions: [
						{
							type: "AssignSubscription",
							triggerDates: [ // should this be removed?
								{
									name: "ContractEffective",
									triggerDate: currentDate,  
								}
							],
							ownerTransfer: { 
								destinationAccountNumber: accountNumber,
								destinationInvoiceAccountNumber: order.accountNumber
							}
						}
					],
					subscriptionNumber: order.subscriptionNumbers[0]
				}
			]
		}
		console.log("assigning the order", req);
		return this.billingService.createOrder({body: req}).then( info => {
			return;
		})
		.catch(err => {
			this.errorHandlerSvc.handleError(err);
			throw err;
		});
	}

	determinePartnerAccountId( acct: Account ): Promise<string>
	{
		if ( this.appUserClaimsSvc.isAssumedRole() )
		{
			return Promise.resolve( this.appUserClaimsSvc.getClaims().accountId );
		}
		else
		{
			return this.adminService.getPartner(acct.partnerId).then(partner => {
				return partner.primeAccount;
			}).catch(err => {
				console.error('can not retrieve partner data', err);
				return Promise.reject( err );
			});
		}
	}

	async assignFreeTrialToCustomerAccount( acct: Account, 
										partnerAccountId: string, 
										productRatePlanChargeId: string, 
										productRatePlanId: string, 
										price: number,
										quantity: number,
										period: number,
										periodType: string): Promise<any>
	{
console.log("Creating a free trial for account", acct, productRatePlanChargeId, productRatePlanId)
		let creatingAsAPartner: boolean = false;
	
		if ( this.appUserClaimsSvc.isAssumedRole() )
		{
			console.log("Creating this trial as a PARTNER")
			creatingAsAPartner = true;
			await this.authService.revertRole();
		}
		// when creating the free trial as a partner, we must create and assign the order
		// when creating as an account admin, we only need to create the order, assignment will be done by the micro
		return this.createAnOrderForSpecifiedProduct( 
				partnerAccountId, 
				productRatePlanChargeId, 
				productRatePlanId, 
				price, 
				quantity,
				period,
				periodType).then( order => {

			if ( creatingAsAPartner == true )
			{
				return this.assignTheOrder(order, acct.accountNumber).then( async done => {
					if ( creatingAsAPartner == true )
						await this.authService.assumeRole("ACCOUNT_ADMIN", acct.accountId);
					this.clearAcctSubscriptionsCache();
					this.remainingLicensesChanged.next();
					return ;
				})
			}
			else
			{
				console.log("This free trial was created as an account admin so no assigment necessary")
				this.clearAcctSubscriptionsCache();
				this.remainingLicensesChanged.next();
				return;
			}
		})
		.catch( async err  => {
			console.error("Error creating free order", err, acct, productRatePlanChargeId, productRatePlanId);
			this.errorHandlerSvc.handleError(err);
			if ( creatingAsAPartner == true && this.appUserClaimsSvc.isAssumedRole() == false )
				await this.authService.assumeRole("ACCOUNT_ADMIN", acct.accountId);
			// create an appropriate message
			let errorMsg = {
				body : {
					message: this.translateService.instant("free-trial.free-trial-generic-error")
				}
			}
			if ( err && err.body && err.body.message )
				errorMsg.body.message = errorMsg.body.message + ": " + err.body.message;
			throw errorMsg;
		});
	}

	// used in conjunction with free trials...determining if an account has ever had a product
	doesCustomerAccountHaveExistingProductSubs( product: string[] ): Promise<boolean>
	{
		let params = {};
		return this.getCachedAcctSubscriptions().then( subscriptionCollection => {
	//	return this.billingService.getSubscriptions(params).then( subscriptionCollection => {
			if ( subscriptionCollection && 
				subscriptionCollection.count > 0 && 
				subscriptionCollection._embedded && 
				subscriptionCollection._embedded.items ) 
			{
				console.log("The subscriptions from the back end", subscriptionCollection._embedded.items);
				let acctSubList: BillingOrder[] = subscriptionCollection._embedded.items; 
			
				for ( let j = 0; j < acctSubList.length; j++ )
				{
					if ( acctSubList[j]._embedded &&
						acctSubList[j]._embedded.ratePlans &&
						acctSubList[j]._embedded.ratePlans.count > 0 && 
						acctSubList[j]._embedded.ratePlans._embedded &&
						acctSubList[j]._embedded.ratePlans._embedded.items )
					{
						let ratePlans: RatePlanInfo[] = acctSubList[j]._embedded.ratePlans._embedded.items;
			
						for ( let l = 0; l < ratePlans.length; l++ )
						{
							for ( var m = 0; m < product.length; m++ )
							{
								if ( ratePlans[l].productId == product[m] )
									return true;
							}
						}
					}
				}
			} else {
				return false;
			}
			// we went through all the subs, there were none found
			return false;
		})
		.catch( err => {
			return false;
		});
	}

	// need to know if an account has a specific sub and specific tier
	doesCustomerAccountHaveExistingProductSubsAndTier( product: string[], tier: string[] ): Promise<boolean>
	{
		return this.getCachedAcctSubTierDetails().then( licensesCollection => {

			if ( licensesCollection && 
				licensesCollection.count > 0 && 
				licensesCollection._embedded && 
				licensesCollection._embedded.items ) 
			{
				console.log("The license collection from the back end", licensesCollection._embedded.items);
				let licenseList: License[] = licensesCollection._embedded.items;

				for ( var i = 0; i < licenseList.length; i++ )
				{
					if ( licenseList[i]._embedded &&
						licenseList[i]._embedded.subscriptions && 
						licenseList[i]._embedded.subscriptions.length > 0 )
					{
						let acctSubList: BillingOrder[] = licenseList[i]._embedded.subscriptions; 

						for ( let j = 0; j < acctSubList.length; j++ )
						{
							if ( acctSubList[j]._embedded &&
								acctSubList[j]._embedded.ratePlans &&
								acctSubList[j]._embedded.ratePlans.count > 0 && 
								acctSubList[j]._embedded.ratePlans._embedded &&
								acctSubList[j]._embedded.ratePlans._embedded.items )
							{
								let ratePlans: RatePlanInfo[] = acctSubList[j]._embedded.ratePlans._embedded.items;
			
								for ( let l = 0; l < ratePlans.length; l++ )
								{
									for ( var m = 0; m < product.length; m++ )
									{
										if ( ratePlans[l].productId == product[m] )
										{
											// found the product but does the tier match?
											let tierName = this.findTierName(licenseList[i])
											if ( tierName != undefined )
											{
												for ( var n = 0; n < tier.length; n++ )
												{
													if ( tierName == tier[n] )
														return true;
												}
											}
										}
									}
								}
							}
						}
					}
				}
			} 
			return false;
		});
	}

	getExistingActiveSubscriptionsSpecifiedForCustomerAccount( products: string[] ): Promise<string[]>
	{
		console.log("determine if there are any active subs of type:", products);
		let licenseFoundList: string[] = [];
		let params = {};
		return this.getCachedAcctSubscriptions().then( subscriptionCollection => {
//		return this.billingService.getSubscriptions(params).then( subscriptionCollection => {
			if ( subscriptionCollection && 
				subscriptionCollection.count > 0 && 
				subscriptionCollection._embedded && 
				subscriptionCollection._embedded.items ) 
			{
				console.log("The subscriptions from the back end", subscriptionCollection._embedded.items);
				let acctSubList: BillingOrder[] = subscriptionCollection._embedded.items; 
			
				for ( let j = 0; j < acctSubList.length; j++ )
				{
					if ( acctSubList[j]._embedded &&
						acctSubList[j]._embedded.ratePlans &&
						acctSubList[j]._embedded.ratePlans.count > 0 && 
						acctSubList[j]._embedded.ratePlans._embedded &&
						acctSubList[j]._embedded.ratePlans._embedded.items )
					{
						let ratePlans: RatePlanInfo[] = acctSubList[j]._embedded.ratePlans._embedded.items;
			
						for ( let l = 0; l < ratePlans.length; l++ )
						{
							// we only want subs that are considered valid. "Remove" is not
							if ( ratePlans[l].lastChangeType == undefined || ratePlans[l].lastChangeType != "Remove" )
							{
								let licenseFound = products.find( prod => {
									return ratePlans[l].productId == prod;
								})
								if ( licenseFound )
								{
									let charges: RatePlanCharge[] = ratePlans[l]._embedded.charges._embedded.items;
					
									for ( var m = 0; m < charges.length; m++ )
									{
										// not convinced this is the final fix
										// if ( charges[m].type === 'Recurring' )
										// {
											let tempSubItem : any = acctSubList[j]; // I need access to a field not in the SDK
						
											let numDays = this.calculateDaysToExpiry(acctSubList[j].termEndDate);

											if ( numDays != 0 ) // the order has not expired so it is valid
											{
												if (licenseFoundList.indexOf(ratePlans[l].productId) === -1 )
													licenseFoundList.push(ratePlans[l].productId)
											}
										// }
									}
								}
							}
						}
		
					}
				}
				return licenseFoundList;		
			}
			else {
				return licenseFoundList;
			}
		})
		// no catch required as the method is handling it
	}


	private getAcctSubscriptionsObservable(): Observable<SubscriptionCollection>
	{
		return Observable.create( async (observer) => {
			let acctSubs: SubscriptionCollection = undefined;
			console.log("***>Requesting the Account subscriptions to cache")
			return this.billingService.getSubscriptions({}).then(acctSubs => {
				return observer.next(acctSubs);
			})
			.catch( err => {
				//we will log and return an empty list
				console.error("There was an error requesting the acct subscriptions", err);
				return observer.next(acctSubs);
			});
		})
		.pipe(
			tap(() => { console.log('getAcctSubscriptionsObservable method executed')}),
			shareReplay()
		);
	}

	getCachedAcctSubscriptions(): Promise<SubscriptionCollection> 
	{
		return new Promise<SubscriptionCollection> ( (resolve, reject) => {
			if ( this.acctSubscriptionsForAccount != undefined )
			{
				//console.log("CACHE ***>Using the existing ACCOUNT cache")
				return resolve(this.acctSubscriptionsForAccount);
			}
			else
			{
				// no cache, get a fresh list
				if ( this.acctSubscriptionsForAccountObservable == undefined )
				{
				//	console.log("CACHE ***>No ACCOUNT Cache requesting new")
					this.acctSubscriptionsForAccountObservable = this.getAcctSubscriptionsObservable();
				}
				this.acctCacheSubscription.add(this.acctSubscriptionsForAccountObservable.subscribe(acctSubs => {
				//	console.log("CACHE Here is the NEW ACCOUNT subs", acctSubs)
					this.acctSubscriptionsForAccount = acctSubs;
					return resolve(acctSubs);
				}));
			}

		});
	}

	private getAcctSubTierDetailsForAccountObservable(): Observable<LicenseCollection>
	{
		return Observable.create( async (observer) => {
			let acctSubs: LicenseCollection = undefined;
			console.log("***>Requesting the LicenseCollection to cache")
			return this.billingService.getLicencesByAccount({$Expand:'subscriptions'}).then( licenseCollection => {
				return observer.next(licenseCollection);
			})
			.catch( err => {
				//we will log and return an empty list
				console.error("There was an error requesting the LicenseCollection", err);
				return observer.next(acctSubs);
			});
		})
		.pipe(
			tap(() => { console.log('acctSubTierDetailsForAccountObservable method executed')}),
			shareReplay()
		);
	}

	getCachedAcctSubTierDetails(): Promise<LicenseCollection> 
	{
		return new Promise<LicenseCollection> ( (resolve, reject) => {
			if ( this.acctSubTierDetailsForAccount != undefined )
			{
				//console.log("CACHE ***>Using the existing ACCOUNT cache")
				return resolve(this.acctSubTierDetailsForAccount);
			}
			else
			{
				// no cache, get a fresh list
				if ( this.acctSubTierDetailsForAccountObservable == undefined )
				{
				//	console.log("CACHE ***>No ACCOUNT Cache requesting new")
					this.acctSubTierDetailsForAccountObservable = this.getAcctSubTierDetailsForAccountObservable();
				}
				this.acctSubTierDetailsCacheSubscription.add(this.acctSubTierDetailsForAccountObservable.subscribe(acctSubs => {
				//	console.log("CACHE Here is the NEW ACCOUNT subs", acctSubs)
					this.acctSubTierDetailsForAccount = acctSubs;
					return resolve(acctSubs);
				}));
			}

		});
	}

	public clearAcctSubscriptionsCache()
	{
	//	console.log("CACHE ***>Clearing the ACCOUNT and USERs subscriptions cache")
	//	console.log("CACHE BEFORE", this.acctSubscriptionsForAccountObservable, this.acctSubscriptionsForAccount,
	//	this.userSubscriptionsForAccountObservable, this.userSubscriptionsForAccount)
		this.acctCacheSubscription.unsubscribe();
		this.acctCacheSubscription = new Subscription();
		this.acctSubscriptionsForAccountObservable = undefined;
		this.acctSubscriptionsForAccount = undefined;
		this.acctSubTierDetailsCacheSubscription.unsubscribe();
		this.acctSubTierDetailsCacheSubscription = new Subscription();
		this.acctSubTierDetailsForAccountObservable = undefined;
		this.acctSubTierDetailsForAccount = undefined;
		this.clearUserSubscriptionsCache();
	//	console.log("CACHE done clearing the ACCOUNT CACHE")
	//	console.log("CACHE AFTER", this.acctSubscriptionsForAccountObservable, this.acctSubscriptionsForAccount,
	//	this.userSubscriptionsForAccountObservable, this.userSubscriptionsForAccount)
	}


	destroySubscriptions() {
		if ( this.subscription) {
			this.subscription.unsubscribe();
		}
		this.userCacheSubscription.unsubscribe();
		this.acctCacheSubscription.unsubscribe();
		this.acctSubTierDetailsCacheSubscription.unsubscribe();
	}


	/*
	This has been added for use by the Support Portal.
	Acceptable params are very specific.
	This has only been tested with a GLOBAL_ADMIN token.
	It returns basic billing info of billing accounts, and can include a customer account number if searching 
	by Subscription Number and the subscription is assigned.
	accounts?$select=accountBasicInfo&$searchKeys=primeAccountId&$search=______
    accounts?$select=accountBasicInfo&$searchKeys=subscriptionNumber&$search=______&$expand=customerAccountNumber
    accounts?$select=accountBasicInfo&$searchKeys=sapId&$search=______
	*/
	public getBasicBillingAccountInfo(selectParam: string, searchKeyParam: string, searchValue: string, expand: string): Promise<any>{
		// putting everthing in options param for now since getBillingAccounts in j-sdk is missing select param
		let params = expand ? {options: { query : {$select: selectParam, $searchKeys: searchKeyParam, $search: searchValue, $expand: expand}}} :
		{options: { query : {$select: selectParam, $searchKeys: searchKeyParam, $search: searchValue}}};

		return this.billingService.getBillingAccounts(params);
	}

}
